// "Pending approvals: N" badge shown in the layout header.
//
// **fn-1.29 scope:** hook is wired to `trpc.approvals.pendingCount`.
// The component shape (sr-only empty + filled "Pending approvals: N")
// is unchanged from fn-1.28; only the hook body is swapped.
//
// **Polling cadence**: `refetchInterval: 5000` matches the acceptance
// criterion: "badge reflects user's `approvals_audit.decision IS NULL`
// count; updates within 5s of a new pending event." A push channel
// (SSE) would deliver lower latency but holds a connection — see
// CLAUDE.md "Lessons learned" #6: HITL UX should not rely on held
// connections (lawyer review windows are week-long; the badge is a
// quick-poll surface).
//
// **Accessibility:** the badge has `aria-live="polite"` so a screen
// reader announces "5 pending approvals" when the number changes —
// HITL is the primary UX for lawyers, so this matters.

import { trpc } from '../trpc-client.js';

/**
 * Polling interval (ms) for the badge. 5000ms matches the
 * acceptance "updates within 5s". Exported so tests can override.
 */
export const PENDING_APPROVALS_POLL_INTERVAL_MS = 5_000;

/**
 * Hook that returns the count of pending approvals visible to the
 * caller. Backed by `trpc.approvals.pendingCount.useQuery` — see
 * `packages/bff/src/trpc/routers/approvals.ts` for the wire shape.
 *
 * **Truncation handling**: when the upstream returns a `truncated`
 * flag (the visible-sessions page held a `next_cursor`), the hook
 * surfaces `Infinity` so the badge renders "X+" rather than a stale
 * concrete count. That keeps the contract honest: the count is a
 * lower bound on overflow pages. **The component checks for non-
 * finite values and renders "many" instead.**
 *
 * **Error fallback**: on query error (401 → AuthGate redirects to
 * login; other errors → render the empty badge). Returning 0 on
 * error is the conservative choice — overstating the count would
 * push users toward false positives.
 */
export function usePendingApprovalsCount(): number {
  const query = trpc.approvals.pendingCount.useQuery(undefined, {
    refetchInterval: PENDING_APPROVALS_POLL_INTERVAL_MS,
    // Re-fetch on window focus so a user returning to the tab sees a
    // fresh count without waiting for the next interval. The default
    // `refetchOnWindowFocus: true` (from `createTrpcReactClient`) already
    // covers this; we don't override.
    retry: false,
  });
  if (query.data === undefined) return 0;
  if (query.data.truncated) return Number.POSITIVE_INFINITY;
  return query.data.count;
}

export interface PendingApprovalsBadgeProps {
  /** Count override — bypasses the hook for storybook / tests. */
  readonly count?: number;
}

/**
 * Renders a badge IF count > 0; otherwise an SR-only "no pending
 * approvals" message so the layout doesn't shift when the count
 * changes from 0 → 1.
 *
 * **Truncated count rendering**: when the hook returns a non-finite
 * count (the upstream signalled `truncated: true` — there are more
 * visible sessions than the first page covered), we render "many"
 * instead of a concrete number. The badge stays in the filled
 * variant so the visual affordance ("you have approvals waiting")
 * works regardless of whether we know the exact number.
 *
 * **Test / Storybook seam**: the `count` prop bypasses the tRPC
 * query entirely. This is the load-bearing reason we delegate the
 * rendering branches to a child component (`PendingApprovalsBadgeView`):
 * the tRPC hook is only invoked when no override is supplied, which
 * keeps the unit test from needing a tRPC Provider.
 */
export function PendingApprovalsBadge(props: PendingApprovalsBadgeProps): React.ReactElement {
  if (props.count !== undefined) {
    return <PendingApprovalsBadgeView count={props.count} />;
  }
  return <PendingApprovalsBadgeLive />;
}

function PendingApprovalsBadgeLive(): React.ReactElement {
  const count = usePendingApprovalsCount();
  return <PendingApprovalsBadgeView count={count} />;
}

function PendingApprovalsBadgeView({ count }: { count: number }): React.ReactElement {
  // Treat NaN / -Infinity / non-numeric as empty (defense — the hook
  // returns `0` on no-data and `Infinity` on truncation; anything else
  // means a caller passed garbage and we render the safe state).
  if (typeof count !== 'number' || Number.isNaN(count)) {
    return renderEmpty();
  }
  if (count === Number.POSITIVE_INFINITY) {
    return renderFilled('many', undefined);
  }
  if (count <= 0) {
    return renderEmpty();
  }
  return renderFilled(`${count}`, count);
}

function renderEmpty(): React.ReactElement {
  return (
    <span
      className="pending-approvals-badge pending-approvals-badge--empty"
      aria-live="polite"
      data-testid="pending-approvals-badge"
    >
      <span className="visually-hidden">No pending approvals</span>
    </span>
  );
}

function renderFilled(label: string, dataCount: number | undefined): React.ReactElement {
  return (
    <output
      className="pending-approvals-badge pending-approvals-badge--filled"
      aria-live="polite"
      data-testid="pending-approvals-badge"
      {...(dataCount !== undefined ? { 'data-count': dataCount } : {})}
    >
      Pending approvals: {label}
    </output>
  );
}
